import React, { useState, useEffect } from "react";
import { DatePicker, Card, Button, Popover, message, Spin, ConfigProvider, Select } from "antd";
import { FilterOutlined, LoadingOutlined } from "@ant-design/icons";
// 1. Sử dụng Day.js thay vì Moment.js để tương thích tốt nhất với Antd 5
import dayjs from "dayjs";
import "dayjs/locale/vi"; 
import customParseFormat from "dayjs/plugin/customParseFormat";
import viVN from "antd/locale/vi_VN";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import API, { 
  SUMMARY_STATS,
  DEPOSIT_STATS,
  TOTAL_SCORE_STATS,
  DAILY_PARTICIPANTS,
  VIP_USER_COUNTS,
  VIP_TOTAL_SCORES
} from "../../network/api";
import "./style.css";

// 2. Cấu hình Dayjs
dayjs.extend(customParseFormat);
dayjs.locale('vi'); 

// --- CẤU HÌNH FORMAT NGÀY THÁNG ---
const DISPLAY_FORMAT = "DD-MM-YYYY"; // Hiển thị trên UI
const API_FORMAT = "DDMMYYYY";       // Gửi xuống API

// --- COMPONENT TÁCH RIÊNG ---
const DateRangeFilter = ({ 
  startDate, 
  endDate, 
  onChange, 
  onApply, 
  onCancel 
}) => {
  
  // Logic xử lý khi chọn "Từ ngày"
  const onFromChange = (date) => {
    if (date) {
      const selectedDate = date.startOf("day");
      // Nếu chọn Ngày Từ > Ngày Đến hiện tại -> Reset Ngày Đến
      if (endDate && selectedDate.isAfter(endDate, 'day')) {
        onChange([selectedDate, null]);
      } else {
        onChange([selectedDate, endDate]);
      }
    } else {
      onChange([null, endDate]);
    }
  };

  // Logic xử lý khi chọn "Đến ngày"
  const onToChange = (date) => {
    if (date) {
      const selectedDate = date.endOf("day");
      // Nếu chọn Ngày Đến < Ngày Từ hiện tại -> Reset Ngày Từ
      if (startDate && selectedDate.isBefore(startDate, 'day')) {
        onChange([null, selectedDate]);
      } else {
        onChange([startDate, selectedDate]);
      }
    } else {
      onChange([startDate, null]);
    }
  };

  return (
    <div className="chart-filter-dropdown">
      <div className="filter-item">
        <label>Từ ngày:</label>
        <DatePicker
          value={startDate}
          placeholder="Chọn ngày"
          onChange={onFromChange}
          format={DISPLAY_FORMAT}
          style={{ width: "100%" }}
          disabledDate={(d) => 
            endDate ? d && d.isAfter(endDate, 'day') : false
          }
        />
      </div>
      <div className="filter-item">
        <label>Đến ngày:</label>
        <DatePicker
          value={endDate}
          placeholder="Chọn ngày"
          onChange={onToChange}
          format={DISPLAY_FORMAT}
          style={{ width: "100%" }}
          disabledDate={(d) => 
            startDate ? d && d.isBefore(startDate, 'day') : false
          }
        />
      </div>
      <div className="filter-buttons">
        <Button onClick={onCancel}>Hủy</Button>
        <Button type="primary" onClick={onApply}>Áp dụng</Button>
      </div>
    </div>
  );
};

// Chart Filter với Metric Selection
const ChartFilter = ({ 
  startDate, 
  endDate, 
  selectedMetric,
  onDateChange, 
  onMetricChange,
  onApply, 
  onCancel 
}) => {
  
  const onFromChange = (date) => {
    if (date) {
      const selectedDate = date.startOf("day");
      if (endDate && selectedDate.isAfter(endDate, 'day')) {
        onDateChange([selectedDate, null]);
      } else {
        onDateChange([selectedDate, endDate]);
      }
    } else {
      onDateChange([null, endDate]);
    }
  };

  const onToChange = (date) => {
    if (date) {
      const selectedDate = date.endOf("day");
      if (startDate && selectedDate.isBefore(startDate, 'day')) {
        onDateChange([null, selectedDate]);
      } else {
        onDateChange([startDate, selectedDate]);
      }
    } else {
      onDateChange([startDate, null]);
    }
  };

  const metricOptions = [
    { value: "participants", label: "Số lượng HV tham gia mỗi ngày" },
    { value: "score", label: "Điểm thưởng mỗi ngày" },
    { value: "deposit", label: "Tổng nạp của HV mỗi ngày" },
  ];

  return (
    <div className="chart-filter-dropdown">
      <div className="filter-item">
        <label>Từ ngày:</label>
        <DatePicker
          value={startDate}
          placeholder="Chọn ngày"
          onChange={onFromChange}
          format={DISPLAY_FORMAT}
          style={{ width: "100%" }}
          disabledDate={(d) => 
            endDate ? d && d.isAfter(endDate, 'day') : false
          }
        />
      </div>
      <div className="filter-item">
        <label>Đến ngày:</label>
        <DatePicker
          value={endDate}
          placeholder="Chọn ngày"
          onChange={onToChange}
          format={DISPLAY_FORMAT}
          style={{ width: "100%" }}
          disabledDate={(d) => 
            startDate ? d && d.isBefore(startDate, 'day') : false
          }
        />
      </div>
      <div className="filter-item">
        <label>Chỉ số hiển thị:</label>
        <Select
          value={selectedMetric}
          onChange={onMetricChange}
          style={{ width: "100%" }}
        >
          {metricOptions.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="filter-buttons">
        <Button onClick={onCancel}>Hủy</Button>
        <Button type="primary" onClick={onApply}>Áp dụng</Button>
      </div>
    </div>
  );
};

// --- MAIN DASHBOARD COMPONENT ---
const Dashboard = () => {
  // --- STATE ---
  const [dateRange1, setDateRange1] = useState([null, null]); 
  const [dateRange2, setDateRange2] = useState([null, null]); 
  // Mặc định lấy 30 ngày gần nhất: từ 30 ngày trước đến ngày hiện tại
  const today = dayjs().startOf("day");
  const thirtyDaysAgo = dayjs().subtract(30, 'day').startOf("day");
  const [chartDateRange, setChartDateRange] = useState([thirtyDaysAgo, today]); 
  const [selectedMetric, setSelectedMetric] = useState("participants"); // Mặc định là "Số lượng HV tham gia mỗi ngày"
  const [vipTurnsDateRange, setVipTurnsDateRange] = useState([null, null]); 
  const [vipBonusDateRange, setVipBonusDateRange] = useState([null, null]); 
  
  // Visibility States
  const [showChartFilter, setShowChartFilter] = useState(false);
  const [showDepositFilter, setShowDepositFilter] = useState(false);
  const [showBonusFilter, setShowBonusFilter] = useState(false);
  const [showVipTurnsFilter, setShowVipTurnsFilter] = useState(false);
  const [showVipBonusFilter, setShowVipBonusFilter] = useState(false);

  // Data States
  const [kpiData, setKpiData] = useState({ totalMembers: 0, totalPlays: 0, bonusPercent: 0, totalDeposit: 0, totalBonus: 0 });
  const [dailyData, setDailyData] = useState([]);
  const [vipTurnsData, setVipTurnsData] = useState([]);
  const [vipBonusData, setVipBonusData] = useState([]);

  // Loading States
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingDeposit, setLoadingDeposit] = useState(false);
  const [loadingBonus, setLoadingBonus] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingVipTurns, setLoadingVipTurns] = useState(false);
  const [loadingVipBonus, setLoadingVipBonus] = useState(false);

  // Helper format cho API (DDMMYYYY)
  const formatDateForAPI = (date) => {
    if (!date || !dayjs.isDayjs(date)) return '';
    return date.format(API_FORMAT);
  };

  // Helper để build params chỉ khi có ngày
  const buildDateParams = (start, end) => {
    const params = {};
    if (start && dayjs.isDayjs(start)) {
      params.fromDate = formatDateForAPI(start);
    }
    if (end && dayjs.isDayjs(end)) {
      params.toDate = formatDateForAPI(end);
    }
    return params;
  };

  // --- API CALLS ---
  const fetchSummaryStats = async () => {
    setLoadingSummary(true);
    try {
      const res = await API.get(SUMMARY_STATS);
      const data = res.data.data || res.data;
      setKpiData(prev => ({ ...prev, totalMembers: data.totalParticipants || 0, totalPlays: data.totalPlays || 0, bonusPercent: data.rewardDepositRatio || 0 }));
    } catch (e) { message.error("Lỗi tải tổng quan"); } finally { setLoadingSummary(false); }
  };

  const fetchDepositStats = async (start, end) => {
    setLoadingDeposit(true);
    try {
      const params = buildDateParams(start, end);
      const res = await API.get(DEPOSIT_STATS, { params });
      setKpiData(prev => ({ ...prev, totalDeposit: (res.data.data || res.data).totalDeposit || 0 }));
    } catch (e) { message.error("Lỗi tải tổng nạp"); } finally { setLoadingDeposit(false); }
  };

  const fetchTotalScoreStats = async (start, end) => {
    setLoadingBonus(true);
    try {
      const params = buildDateParams(start, end);
      const res = await API.get(TOTAL_SCORE_STATS, { params });
      const data = res.data.data || res.data;
      setKpiData(prev => ({
        ...prev,
        totalBonus: data.totalScore || 0,
        bonusPercent: prev.totalDeposit > 0 ? ((data.totalScore / prev.totalDeposit) * 100).toFixed(2) : 0
      }));
    } catch (e) { message.error("Lỗi tải điểm thưởng"); } finally { setLoadingBonus(false); }
  };

  const fetchDailyParticipants = async (start, end, type = 'participants') => {
    setLoadingDaily(true);
    try {
      const params = { ...buildDateParams(start, end), type };
      const res = await API.get(DAILY_PARTICIPANTS, { params });
      setDailyData((res.data.data || res.data) || []);
    } catch (e) { message.error("Lỗi tải biểu đồ ngày"); setDailyData([]); } finally { setLoadingDaily(false); }
  };

  const fetchVipUserCounts = async (start, end) => {
    setLoadingVipTurns(true);
    try {
      const params = buildDateParams(start, end);
      const res = await API.get(VIP_USER_COUNTS, { params });
      setVipTurnsData(((res.data.data || res.data) || []).map(i => ({ level: `${i.vipLevel}`, value: i.value || 0 })));
    } catch (e) { message.error("Lỗi tải VIP turns"); setVipTurnsData([]); } finally { setLoadingVipTurns(false); }
  };

  const fetchVipTotalScores = async (start, end) => {
    setLoadingVipBonus(true);
    try {
      const params = buildDateParams(start, end);
      const res = await API.get(VIP_TOTAL_SCORES, { params });
      setVipBonusData(((res.data.data || res.data) || []).map(i => ({ level: `${i.vipLevel}`, value: i.value || 0 })));
    } catch (e) { message.error("Lỗi tải VIP bonus"); setVipBonusData([]); } finally { setLoadingVipBonus(false); }
  };

  useEffect(() => {
    fetchSummaryStats();
    fetchDepositStats(dateRange1[0], dateRange1[1]);
    fetchTotalScoreStats(dateRange2[0], dateRange2[1]);
    fetchDailyParticipants(chartDateRange[0], chartDateRange[1], selectedMetric);
    fetchVipUserCounts(vipTurnsDateRange[0], vipTurnsDateRange[1]);
    fetchVipTotalScores(vipBonusDateRange[0], vipBonusDateRange[1]);
    // eslint-disable-next-line
  }, []);

  // --- HANDLERS ---
  const handleApplyDepositFilter = () => { 
    fetchDepositStats(dateRange1[0], dateRange1[1]); 
    setShowDepositFilter(false); 
  };

  const handleApplyBonusFilter = () => { 
    fetchTotalScoreStats(dateRange2[0], dateRange2[1]); 
    setShowBonusFilter(false); 
  };

  const handleApplyChartFilter = () => { 
    fetchDailyParticipants(chartDateRange[0], chartDateRange[1], selectedMetric); 
    setShowChartFilter(false); 
  };

  // Hàm lấy title theo metric
  const getChartTitleByMetric = (metric) => {
    const metricMap = {
      participants: "Số lượng HV tham gia mỗi ngày",
      score: "Điểm thưởng mỗi ngày",
      deposit: "Tổng nạp của HV mỗi ngày",
    };
    return metricMap[metric] || "Số lượng HV tham gia mỗi ngày";
  };

  const handleApplyVipTurnsFilter = () => { 
    fetchVipUserCounts(vipTurnsDateRange[0], vipTurnsDateRange[1]); 
    setShowVipTurnsFilter(false); 
  };

  const handleApplyVipBonusFilter = () => { 
    fetchVipTotalScores(vipBonusDateRange[0], vipBonusDateRange[1]); 
    setShowVipBonusFilter(false); 
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  return (
    <ConfigProvider locale={viVN}>
      <div className="dashboard-container">
        <div className="dashboard-header"><h1 className="dashboard-title">Dashboard Hội Viên</h1></div>
        <div className="logo-brand-section"><img src="/logo.webp" alt="DT68 Logo" className="brand-logo" /></div>

        <div className="kpi-cards">
          <Card className="kpi-card"><div className="kpi-title">Số lượng hội viên tham gia</div><div className="kpi-value">{loadingSummary ? <Spin indicator={antIcon} /> : kpiData.totalMembers.toLocaleString()}</div></Card>
          <Card className="kpi-card"><div className="kpi-title">Tổng lượt chơi</div><div className="kpi-value">{loadingSummary ? <Spin indicator={antIcon} /> : kpiData.totalPlays.toLocaleString()}</div></Card>
          <Card className="kpi-card"><div className="kpi-title">% Điểm thưởng / Tổng nạp</div><div className="kpi-value">{loadingBonus || loadingDeposit ? <Spin indicator={antIcon} /> : `${kpiData.bonusPercent}%`}</div></Card>

          <Card className="kpi-card">
            <div className="kpi-title">
              Tổng nạp hội viên
              <Popover 
                trigger="click" open={showDepositFilter} onOpenChange={setShowDepositFilter}
                content={
                  <DateRangeFilter 
                    startDate={dateRange1[0]} endDate={dateRange1[1]} 
                    onChange={setDateRange1} 
                    onApply={handleApplyDepositFilter} 
                    onCancel={() => setShowDepositFilter(false)} 
                  />
                }
              >
                <FilterOutlined className="filter-icon" style={{ cursor: "pointer" }} />
              </Popover>
            </div>
            <div className="kpi-value">{loadingDeposit ? <Spin indicator={antIcon} /> : kpiData.totalDeposit.toLocaleString()}</div>
          </Card>

          <Card className="kpi-card">
            <div className="kpi-title">
              Tổng số điểm thưởng
              <Popover 
                trigger="click" open={showBonusFilter} onOpenChange={setShowBonusFilter}
                content={
                  <DateRangeFilter 
                    startDate={dateRange2[0]} endDate={dateRange2[1]} 
                    onChange={setDateRange2} 
                    onApply={handleApplyBonusFilter} 
                    onCancel={() => setShowBonusFilter(false)} 
                  />
                }
              >
                <FilterOutlined className="filter-icon" style={{ cursor: "pointer" }} />
              </Popover>
            </div>
            <div className="kpi-value">{loadingBonus ? <Spin indicator={antIcon} /> : kpiData.totalBonus.toLocaleString()}</div>
          </Card>
        </div>

        {/* Charts */}
        <div className="chart-section">
          <div className="chart-header">
            <div>
              <h2 className="chart-title">Số lượng theo dõi mỗi ngày</h2>
              <p className="chart-subtitle">
                {getChartTitleByMetric(selectedMetric)} ({chartDateRange[0] ? chartDateRange[0].format("DD/MM/YYYY") : "..."} - {chartDateRange[1] ? chartDateRange[1].format("DD/MM/YYYY") : "..."})
              </p>
            </div>
            <Popover 
              trigger="click" open={showChartFilter} onOpenChange={setShowChartFilter}
              content={
                <ChartFilter 
                  startDate={chartDateRange[0]} 
                  endDate={chartDateRange[1]} 
                  selectedMetric={selectedMetric}
                  onDateChange={setChartDateRange}
                  onMetricChange={setSelectedMetric}
                  onApply={handleApplyChartFilter} 
                  onCancel={() => setShowChartFilter(false)} 
                />
              }
            >
              <Button icon={<FilterOutlined />} />
            </Popover>
          </div>
          <div className="chart-container-recharts">
            {loadingDaily ? <div style={{height:300, display:'flex', alignItems:'center', justifyContent:'center'}}><Spin indicator={antIcon}/></div> : 
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={0} /><YAxis /><Tooltip /><Bar dataKey="value" fill="#4a90e2" /></BarChart>
              </ResponsiveContainer>
            }
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <div><h2 className="chart-title">Số lượt theo cấp VIP</h2></div>
            <Popover 
              trigger="click" open={showVipTurnsFilter} onOpenChange={setShowVipTurnsFilter}
              content={
                <DateRangeFilter 
                  startDate={vipTurnsDateRange[0]} endDate={vipTurnsDateRange[1]} 
                  onChange={setVipTurnsDateRange} 
                  onApply={handleApplyVipTurnsFilter} 
                  onCancel={() => setShowVipTurnsFilter(false)} 
                />
              }
            >
              <Button icon={<FilterOutlined />} />
            </Popover>
          </div>
          <div className="chart-container-recharts">
            {loadingVipTurns ? <div style={{height:300, display:'flex', alignItems:'center', justifyContent:'center'}}><Spin indicator={antIcon}/></div> : 
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={vipTurnsData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="level" angle={-45} textAnchor="end" height={80} interval={0} /><YAxis /><Tooltip /><Bar dataKey="value" fill="#4a90e2" /></BarChart>
              </ResponsiveContainer>
            }
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <div><h2 className="chart-title">Tổng điểm thưởng theo cấp VIP</h2></div>
            <Popover 
              trigger="click" open={showVipBonusFilter} onOpenChange={setShowVipBonusFilter}
              content={
                <DateRangeFilter 
                  startDate={vipBonusDateRange[0]} endDate={vipBonusDateRange[1]} 
                  onChange={setVipBonusDateRange} 
                  onApply={handleApplyVipBonusFilter} 
                  onCancel={() => setShowVipBonusFilter(false)} 
                />
              }
            >
              <Button icon={<FilterOutlined />} />
            </Popover>
          </div>
          <div className="chart-container-recharts">
            {loadingVipBonus ? <div style={{height:300, display:'flex', alignItems:'center', justifyContent:'center'}}><Spin indicator={antIcon}/></div> : 
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={vipBonusData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="level" angle={-45} textAnchor="end" height={80} interval={0} /><YAxis /><Tooltip /><Bar dataKey="value" fill="#4a90e2" /></BarChart>
              </ResponsiveContainer>
            }
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Dashboard;